// Pure helpers behind "Tier config: privilege list + coin % per tier"
// (mdf-bo-fn#292, spec docs/specs/2026-09-30-tier-config-design.md §4 HTTP,
// §5 FE).
//
// Vue-free / axios-free on purpose: this repo's jest cannot mount a .vue file
// (see tests/unit/skuWmsOptionsSource.spec.js), so everything decidable — what
// an API privilege row becomes in the editor, what the editor sends back, which
// rows a PUT would insert/update/delete, and which values are refused before a
// request is spent — lives here where a spec can call it directly.
// MemberLevels.vue only renders and calls.
//
// --------------------------------------------------------------------------
// THE BACKEND CONTRACT (§4). Built in parallel with this screen, so these
// bytes are the agreement, not an observation of a running server.
//
//   GET /api/v1/bo/member-levels
//     each level additionally carries
//       cashback_percent : number 0..100   (0 = not set, falls through to the
//                                           store default — §1.2)
//       privileges       : [{id, sort_order, icon, label_th, label_en,
//                            is_active}]   ordered by sort_order
//     On a database that has not run the migration yet the guard answers
//     privileges: [] and cashback_percent: 0 (§2, invariant 5) — so an absent
//     or null `privileges` is a normal answer, never an error.
//
//   POST / PUT /api/v1/bo/member-levels[/:id]
//     accept the same two fields. The PUT REPLACES the whole list of that
//     tier (diff by id happens server-side in one tx: id present = update,
//     absent = insert, missing from the list = delete). `privileges` omitted
//     entirely = list unchanged, which is why the editor must send the full
//     list whenever it sends anything at all.
//     400 {code:"bad_cashback_percent"} — outside 0..100
//     400 {code:"bad_privilege"}        — a row with an empty label_th
//
// --------------------------------------------------------------------------

// The fixed icon set §5 nails down. It is a closed list because the member web
// renders it too (member-fn `src/utils/tierIcons.js`): an icon this array does
// not contain would reach the customer as a blank square.
export const PRIVILEGE_ICONS = [
  "truck",
  "gift",
  "star",
  "crown",
  "headset",
  "cake",
  "percent",
  "coin"
];

// FontAwesome 5.6.3 free-solid classes (public/index.html loads it) for each
// name above. Kept next to the list so the two cannot drift apart.
const PRIVILEGE_ICON_CLASSES = {
  truck: "fa fa-truck",
  gift: "fa fa-gift",
  star: "fa fa-star",
  crown: "fa fa-crown",
  headset: "fa fa-headset",
  cake: "fa fa-birthday-cake",
  percent: "fa fa-percent",
  coin: "fa fa-coins"
};

// The neutral placeholder for "no icon" and for an icon this build does not
// know. Not an empty string: a row whose icon cell renders nothing looks like a
// broken layout, and an unknown icon is a thing to notice, not to hide.
export const PRIVILEGE_ICON_FALLBACK_CLASS = "fa fa-circle text-muted";

// Error keys validatePrivileges / cashbackPercentError answer with. They are
// i18n suffixes under `member_level.*` — the caller prefixes, so these strings
// stay comparable in a spec.
export const ERR_EMPTY_LABEL_TH = "empty_label_th";
export const ERR_UNKNOWN_ICON = "unknown_icon";
export const ERR_CASHBACK_RANGE = "range";
export const ERR_CASHBACK_NAN = "nan";

function trimmed(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

// positiveId: the id of a row that already exists server-side, or null.
//
// null rather than 0, because §4 spells "insert this row" as an ABSENT id — and
// an id of 0 sent as a number would read as "update row 0" to a Go handler
// binding into int64. Everything that leaves this module therefore either has
// a positive id or has no `id` key at all.
function positiveId(value) {
  if (value === null || value === undefined || value === "") return null;
  const id = Number(value);
  if (!isFinite(id) || id <= 0) return null;
  return Math.trunc(id);
}

// is_active defaults to TRUE (§2 DDL `DEFAULT true`): a privilege typed into a
// fresh row is meant to be shown. Only an explicit falsey answer — including
// the strings a form/proxy can hand back — turns it off.
function activeFlag(value) {
  if (value === undefined || value === null) return true;
  if (value === false || value === 0) return false;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "" || s === "false" || s === "0" || s === "no") return false;
    return true;
  }
  return !!value;
}

// isBlankRow: a row with nothing to show. Dropped by normalizePrivileges.
//
// Includes rows that DO have an id: clearing every field of an existing row is
// how the editor's remove button and a hand-emptied row both say "delete this",
// and a full-list PUT expresses a delete precisely by leaving the row out.
function isBlankRow(row) {
  return (
    trimmed(row.label_th) === "" &&
    trimmed(row.label_en) === "" &&
    trimmed(row.icon) === ""
  );
}

// normalizePrivileges turns whatever the editor (or the API) is holding into
// the exact list §4 accepts.
//
// ARRAY ORDER IS THE ORDER. sort_order is overwritten with the row's index
// rather than respected, because the ▲/▼ buttons move rows inside the array and
// leave every sort_order stale the moment they are pressed. Rows that arrive
// from a GET must therefore be put in order BEFORE they get here — that is what
// privilegesFromApi is for.
export function normalizePrivileges(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  rows.forEach((raw) => {
    if (!raw || typeof raw !== "object") return;
    if (isBlankRow(raw)) return;
    const row = {
      sort_order: out.length,
      icon: trimmed(raw.icon),
      label_th: trimmed(raw.label_th),
      label_en: trimmed(raw.label_en),
      is_active: activeFlag(raw.is_active)
    };
    const id = positiveId(raw.id);
    if (id !== null) row.id = id;
    out.push(row);
  });
  return out;
}

// privilegesFromApi: a GET's `privileges` into editor rows.
//
// Sorts by the server's sort_order first, so that the index-based sort_order
// normalizePrivileges assigns reproduces the order the tier was saved with
// instead of silently reshuffling it on the next save. A missing/null list (an
// un-migrated database, §2) is an empty editor, not a crash.
export function privilegesFromApi(rows) {
  if (!Array.isArray(rows)) return [];
  const ordered = rows
    .filter((row) => row && typeof row === "object")
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const sa = Number(a.row.sort_order);
      const sb = Number(b.row.sort_order);
      const na = isFinite(sa) ? sa : 0;
      const nb = isFinite(sb) ? sb : 0;
      // Stable: equal sort_order keeps the order the server sent.
      if (na !== nb) return na - nb;
      return a.index - b.index;
    })
    .map((entry) => entry.row);
  return normalizePrivileges(ordered);
}

function sameRow(a, b) {
  return (
    a.icon === b.icon &&
    a.label_th === b.label_th &&
    a.label_en === b.label_en &&
    a.is_active === b.is_active &&
    a.sort_order === b.sort_order
  );
}

// privilegesDiff: what saving this editor would do, by id.
//
// FOR DISPLAY ONLY — the request always carries the full list (§4), so nothing
// here decides what is sent. It exists so the dialog can say "1 added, 2
// changed, 1 removed" before the operator commits, which is the one thing a
// replace-the-whole-list endpoint cannot be asked about afterwards.
//
// Both sides are normalized first, so a pure reorder shows up as `updated`
// (sort_order is part of the row) and a blank row on either side is ignored.
// A row carrying an id the original does not have counts as `updated`, because
// that is what the server would attempt with it.
export function privilegesDiff(original, edited) {
  const before = normalizePrivileges(original);
  const after = normalizePrivileges(edited);
  const beforeById = {};
  before.forEach((row) => {
    if (row.id !== undefined) beforeById[row.id] = row;
  });
  const afterIds = {};
  const added = [];
  const updated = [];
  after.forEach((row) => {
    if (row.id === undefined) {
      added.push(row);
      return;
    }
    afterIds[row.id] = true;
    const previous = beforeById[row.id];
    if (!previous || !sameRow(previous, row)) updated.push(row);
  });
  const removed = before.filter(
    (row) => row.id !== undefined && !afterIds[row.id]
  );
  return { added, updated, removed };
}

// validatePrivileges: the error keys this list would be rejected for, deduped
// and in a stable order.
//
// `empty_label_th` is the blocking one — it is the 400 `bad_privilege` the
// server answers (§4), so the editor must not spend a request on it.
// `unknown_icon` is a WARNING: an icon outside PRIVILEGE_ICONS is accepted by
// the schema (`icon varchar(64)`) and the member web falls back to a neutral
// glyph, so it is flagged and saved rather than refused. Keeping both in one
// array — rather than throwing on the first — lets the dialog report everything
// wrong with the list in a single pass.
export function validatePrivileges(rows) {
  const list = normalizePrivileges(rows);
  const errors = [];
  let emptyLabel = false;
  let unknownIcon = false;
  list.forEach((row) => {
    if (row.label_th === "") emptyLabel = true;
    if (row.icon !== "" && PRIVILEGE_ICONS.indexOf(row.icon) === -1) {
      unknownIcon = true;
    }
  });
  if (emptyLabel) errors.push(ERR_EMPTY_LABEL_TH);
  if (unknownIcon) errors.push(ERR_UNKNOWN_ICON);
  return errors;
}

// cashbackPercentError: null when this value may be sent.
//
// An EMPTY value is null (fine), not "nan": §4 reads an omitted
// cashback_percent as "unchanged" (invariant 2), so a cleared el-input-number
// means "leave the tier alone" and must not block the save of the rest of the
// form. Fractions are legal — 7.5 is stored as 7.50 (numeric(5,2)) — so only
// the 0..100 bound and genuinely non-numeric input are refused.
export function cashbackPercentError(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  if (typeof value === "boolean") return ERR_CASHBACK_NAN;
  const n = Number(value);
  if (!isFinite(n)) return ERR_CASHBACK_NAN;
  if (n < 0 || n > 100) return ERR_CASHBACK_RANGE;
  return null;
}

// privilegeIconClass: the <i> class for an icon name.
//
// Unknown and empty both answer the neutral placeholder, so the icon column of
// a row saved by an older/newer build still renders something with a shape.
// Own-property lookup, not `map[key] ||`: "constructor" / "toString" would
// otherwise resolve to a function off Object.prototype and be rendered as a
// class attribute.
export function privilegeIconClass(name) {
  const key = trimmed(name);
  if (key === "") return PRIVILEGE_ICON_FALLBACK_CLASS;
  if (!Object.prototype.hasOwnProperty.call(PRIVILEGE_ICON_CLASSES, key)) {
    return PRIVILEGE_ICON_FALLBACK_CLASS;
  }
  return PRIVILEGE_ICON_CLASSES[key];
}

export default {
  PRIVILEGE_ICONS,
  PRIVILEGE_ICON_FALLBACK_CLASS,
  ERR_EMPTY_LABEL_TH,
  ERR_UNKNOWN_ICON,
  ERR_CASHBACK_RANGE,
  ERR_CASHBACK_NAN,
  normalizePrivileges,
  privilegesFromApi,
  privilegesDiff,
  validatePrivileges,
  cashbackPercentError,
  privilegeIconClass
};
